// @ts-nocheck

import { isBetEligible } from '@winamax/missions-lib';
import _ from 'lodash';

import { type StateType } from '../../../states';
import { type BasketState } from '../../../states/basket';
import { type BasketItemState } from '../../../states/basket';
import { type MatchState } from '../../../states/entities/matches';
import { BasketModes, BasketStatuses } from '../../../types/basket';
import { selectIsBoomEnabled } from '../selectors/runtime.selector';
import { selectBoomStepsState } from '../selectors/steps/stepsPrimary.selector';
import { selectBoomStepsOrderState } from '../selectors/stepsOrder/stepsOrderPrimary.selector';
import { selectBoomUserProgress } from '../selectors/user/userPrimary.selector';
import { selectIsBoomUserOptIn } from '../selectors/user.selector';
import { BoomGlobalStatuses } from '../types/global';
import { BoomStepStatuses } from '../types/steps';

const getEligibleSteps = (globalState, oddsId) => {
  const { basket, entities, boom } = globalState;
  const items = basket.items;
  const stepsOrder = selectBoomStepsOrderState(globalState);
  const steps = selectBoomStepsState(globalState);
  const userProgress = selectBoomUserProgress(globalState);
  const progressOpen = Object.keys(userProgress).filter((key) => userProgress[key].status === BoomStepStatuses.OPEN);

  const nextAvailableSteps = stepsOrder.filter((f) => progressOpen.includes(f));

  const item = items[`${oddsId ?? 0}`];

  const eligibilityOptions = {
    basket,
    entities,
    item: item ?? null,
  };

  const stepsTmp = [];

  const { eligibility } = steps?.[nextAvailableSteps[0]] ?? {};

  if (eligibility) {
    const isEligible = isBetEligible({
      eligibility,
      ...eligibilityOptions,
    });

    if (isEligible) {
      const stepId = nextAvailableSteps[0];

      if (userProgress[stepId].status === BoomStepStatuses.OPEN) {
        stepsTmp.push({
          skinId: boom.global.skinId,
          stepId,
        });
      }
    }
  }

  return stepsTmp.length && boom?.global?.status === BoomGlobalStatuses.RUNNING ? stepsTmp[0] : null;
};

const getIsMatchStartDateValid = (matchState: MatchState) => {
  if (matchState) {
    const { matchStart = Infinity } = matchState;

    return matchStart * 1000 - Date.now() < 1000 * 60 * 60 * 24 * 2; // match's start date must be in less than 48h
  }

  return false;
};

const getIsBasketItemReadyForEligibilityCheck = (
  basketItem?: BasketItemState,
  matchesState: { [matchId: number]: MatchState } = {},
) => {
  const { matchId } = basketItem ?? {};
  const match = matchesState[matchId] ?? {};

  return getIsMatchStartDateValid(match);
};

const getIsBasketReadyForEligibilityCheck = (
  { items: basketItems }: BasketState = {},
  matchesState: { [matchId: number]: MatchState } = {},
) =>
  Object.keys(basketItems).every((itemId) =>
    getIsBasketItemReadyForEligibilityCheck(basketItems[itemId] ?? {}, matchesState),
  );

const recomputeNextBoomBasketState = (nextBasketState: BasketState, globalState: StateType) => {
  const { isCashout, status: basketStatus } = nextBasketState;
  const isBoomEnabled = selectIsBoomEnabled(globalState);
  const isUserOptIn = selectIsBoomUserOptIn(globalState);
  const basketMode = nextBasketState.mode;

  if (!isBoomEnabled || !isUserOptIn || isCashout || basketStatus === BasketStatuses.VALIDATED) {
    return nextBasketState;
  }

  const {
    entities: { matches: previousMatches = {} } = {},
    basket: previousBasket = {},
    boom: previousBoomState,
  } = globalState ?? {};

  const tmpBasketState = nextBasketState ?? previousBasket;
  const allRelatedMatches = Object.keys(nextBasketState.items).map(
    (item) => previousMatches[nextBasketState.items[item].matchId],
  );
  const hasAMatchOvertime = allRelatedMatches.some(
    (relatedMatch) => relatedMatch.matchStart * 1000 > previousBoomState.global.operationEndDate,
  );

  const isBasketReadyForEligibilityCheck = getIsBasketReadyForEligibilityCheck(nextBasketState, previousMatches);

  let nextBasketBoom = null;

  if (isBasketReadyForEligibilityCheck) {
    nextBasketBoom = hasAMatchOvertime
      ? undefined
      : getEligibleSteps({
          ...globalState,
          basket: tmpBasketState,
        }) ?? null;
  }

  const hasBasketBoomChanged = !_.isEqual(nextBasketBoom, tmpBasketState.boom);
  let hasBasketItemsChanged = false;

  const { items: basketItems } = tmpBasketState;
  const resetedBasketItems = {};

  for (const itemId of Object.keys(basketItems)) {
    resetedBasketItems[itemId] = basketItems[itemId];
    delete resetedBasketItems[itemId].boom;
  }

  const nextItems = { ...resetedBasketItems };

  const sortingCallback = (a, b) => a.sortBy - b.sortBy;

  const basketItemsAsArr = Object.keys(resetedBasketItems)
    .map((itemId) => resetedBasketItems[itemId])
    .sort(sortingCallback);

  for (const basketItem of basketItemsAsArr) {
    const nextBasketItemsAsArr = Object.keys(nextItems)
      .map((itemId) => nextItems[itemId])
      .sort(sortingCallback);
    const { oddId: itemId } = basketItem;

    const isBasketItemReadyForEligibilityCheck = getIsBasketItemReadyForEligibilityCheck(basketItem, previousMatches);

    let eligibleStep = null;

    if (isBasketItemReadyForEligibilityCheck) {
      eligibleStep = getEligibleSteps(
        {
          ...globalState,
          basket: {
            ...tmpBasketState,
            nextItems,
          },
        },
        itemId,
      );
    }

    if (eligibleStep && basketMode === BasketModes.SIMPLE) {
      const eligibleStepThatFits = !nextBasketItemsAsArr
        .filter((basketItem) => !!basketItem.boom)
        .some(
          (basketItem) =>
            basketItem.boom.skinId === eligibleStep?.skinId && basketItem.boom.stepId === eligibleStep?.stepId,
        );

      const isMatchStartInTime =
        previousMatches[nextItems[itemId].matchId].matchStart * 1000 < previousBoomState?.global?.operationEndDate &&
        previousBoomState?.global.status === BoomGlobalStatuses.RUNNING;

      if (eligibleStepThatFits) {
        nextItems[itemId] = {
          ...nextItems[itemId],
          isBoomActive: nextItems[itemId].isBoomActive ?? isMatchStartInTime,
          boom: isMatchStartInTime ? eligibleStep : undefined,
        };

        hasBasketItemsChanged = true;
      } else {
        delete nextItems[itemId].boom;
        nextItems[itemId].isBoomActive = false;
        hasBasketItemsChanged = true;
      }
    } else {
      delete nextItems[itemId].boom;
      nextItems[itemId].isBoomActive = false;
      hasBasketItemsChanged = true;
    }
  }

  let nextState = tmpBasketState;

  if (hasBasketBoomChanged) {
    nextState = {
      ...nextState,
      boom: nextBasketBoom,
      isBoomActive: hasAMatchOvertime ? false : !!nextBasketBoom,
    };
  }

  if (hasBasketItemsChanged) {
    nextState = { ...nextState, items: nextItems };
  }

  return nextState;
};

export default recomputeNextBoomBasketState;
